import type { ReviewEvidence, ReviewField } from '@console/types'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT AN ITEM IS CALLED, versus what it is identified by — `LGH-023`.
 *
 * Rendering the real platform for the first time showed the operator being
 * asked to choose between **`500140000000113017`** and
 * **`500140000000113049`** — two 18-digit Zoho record ids — while
 * `evidence.catalogName` carried *"Cable"* and *"Cable Tray 200mm (3m
 * length)"* in the same payload.
 *
 * ── ITEM ONLY, AND THAT IS THE WHOLE RULE ─────────────────────────────────
 * A unit price's grounded evidence is **also** `catalog` — line 1's price is
 * evidenced *"the list price on your item DUC 2.5 SC"*, with `catalogName`
 * `DUC 2.5 SC`. Applying this to every field would render a price as an item
 * name, which is worse than the id.
 *
 * **The catalogue name names the ITEM. For a quantity or a price the value IS
 * the value**, and the catalogue is only where the number came from.
 *
 * ── WHY THIS IS PRESENTATION AND NOT A SECOND SOURCE OF TRUTH ─────────────
 * Both strings are served; this chooses which one a human reads, which is
 * `R-067`-safe. Nothing is composed and nothing is inferred — and the choice
 * is **stable whichever way `value`'s semantics settle**: if the platform
 * later puts the name in `value`, `catalogName` still holds the name and this
 * still renders correctly. The question is asked separately
 * (`REQ-lighthouse-what-value-means-for-an-item`), not answered here.
 *
 * Falls back to `value` when there is no catalogue behind the field — an
 * `rfq_span` item is the customer's own words and has no catalogue name.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function readableItemValue(
  field: ReviewField,
  value: string,
  evidence: ReviewEvidence | undefined,
): string {
  if (field !== 'item' || evidence === undefined) return value
  return 'catalogName' in evidence && evidence.catalogName !== '' ? evidence.catalogName : value
}
